import { useState, useEffect, useCallback, useRef } from 'react';
import {
  type UserProfile,
  type LoginInput,
  type RegisterInput,
  type MfaChallengeInput,
  login as authLogin,
  register as authRegister,
  confirmMfa as authConfirmMfa,
  refresh as authRefresh,
  signOut as authSignOut,
  getTokens,
  isTokenExpiringSoon,
  clearTokens,
} from '../services/authService';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AuthState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mfaRequired: boolean;
  mfaSession: string | null;
}

export interface UseAuthReturn extends AuthState {
  login: (input: LoginInput) => Promise<{ mfaRequired: boolean; session?: string }>;
  register: (input: RegisterInput) => Promise<{ totpSetupUri?: string }>;
  confirmMfa: (input: MfaChallengeInput) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  checkAuth: () => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const REFRESH_CHECK_INTERVAL_MS = 60 * 1000; // Check every minute
const TOKEN_EXPIRY_THRESHOLD_MS = 5 * 60 * 1000; // Refresh when < 5 min remaining

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    mfaRequired: false,
    mfaSession: null,
  });

  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Auto-refresh logic ─────────────────────────────────────────────────

  const startRefreshInterval = useCallback(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    refreshIntervalRef.current = setInterval(async () => {
      if (isTokenExpiringSoon(TOKEN_EXPIRY_THRESHOLD_MS)) {
        try {
          await authRefresh();
        } catch {
          // Refresh failed — session expired, clear state
          clearTokens();
          setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            mfaRequired: false,
            mfaSession: null,
          });
        }
      }
    }, REFRESH_CHECK_INTERVAL_MS);
  }, []);

  const stopRefreshInterval = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  // ─── Initialize: attempt token refresh on mount ─────────────────────────

  useEffect(() => {
    let cancelled = false;

    async function init() {
      // Check if we already have tokens in memory (e.g. after a re-render)
      const existingTokens = getTokens();
      if (existingTokens && existingTokens.expiresAt > Date.now()) {
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            isAuthenticated: true,
            isLoading: false,
          }));
          startRefreshInterval();
        }
        return;
      }

      // Attempt silent refresh via HttpOnly cookie
      try {
        await authRefresh();
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            isAuthenticated: true,
            isLoading: false,
          }));
          startRefreshInterval();
        }
      } catch {
        // No valid refresh token — user is not authenticated
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            isAuthenticated: false,
            isLoading: false,
          }));
        }
      }
    }

    init();

    return () => {
      cancelled = true;
      stopRefreshInterval();
    };
  }, [startRefreshInterval, stopRefreshInterval]);

  // ─── Auth actions ───────────────────────────────────────────────────────

  const login = useCallback(
    async (input: LoginInput): Promise<{ mfaRequired: boolean; session?: string }> => {
      const response = await authLogin(input);

      if (response.mfaRequired) {
        setState((prev) => ({
          ...prev,
          mfaRequired: true,
          mfaSession: response.session ?? null,
        }));
        return { mfaRequired: true, session: response.session };
      }

      // Login complete (no MFA needed — shouldn't happen in this app, but handle it)
      if (response.user) {
        setState({
          user: response.user,
          isAuthenticated: true,
          isLoading: false,
          mfaRequired: false,
          mfaSession: null,
        });
        startRefreshInterval();
      }

      return { mfaRequired: false };
    },
    [startRefreshInterval],
  );

  const register = useCallback(
    async (input: RegisterInput): Promise<{ totpSetupUri?: string }> => {
      const response = await authRegister(input);
      return { totpSetupUri: response.totpSetupUri };
    },
    [],
  );

  const confirmMfa = useCallback(
    async (input: MfaChallengeInput): Promise<void> => {
      const response = await authConfirmMfa(input);

      if (response.user) {
        setState({
          user: response.user,
          isAuthenticated: true,
          isLoading: false,
          mfaRequired: false,
          mfaSession: null,
        });
        startRefreshInterval();
      }
    },
    [startRefreshInterval],
  );

  const logout = useCallback(async (): Promise<void> => {
    stopRefreshInterval();
    await authSignOut();
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      mfaRequired: false,
      mfaSession: null,
    });
  }, [stopRefreshInterval]);

  const refreshAction = useCallback(async (): Promise<void> => {
    await authRefresh();
  }, []);

  const checkAuth = useCallback(() => {
    const tokens = getTokens();
    if (tokens && tokens.expiresAt > Date.now()) {
      setState((prev) => ({
        ...prev,
        isAuthenticated: true,
        isLoading: false,
      }));
      startRefreshInterval();
    }
  }, [startRefreshInterval]);

  // ─── Return ─────────────────────────────────────────────────────────────

  return {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    mfaRequired: state.mfaRequired,
    mfaSession: state.mfaSession,
    login,
    register,
    confirmMfa,
    logout,
    refresh: refreshAction,
    checkAuth,
  };
}
